import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Auth, connectAuthEmulator } from '@angular/fire/auth';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { environment } from '@sh/env/environment';
import { SeoService } from '@sh/services/seo/seo.service';
import { combineLatest, filter, map, startWith } from 'rxjs';
import { ProfileFacadeService } from './facades/profile-facade.service';

type loadType = 'profile' | 'module';
interface QuickAction {
  label: string;
  path: string;
  icon: string;
  search?: boolean;
  external?: boolean;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  title = 'Study Heroes';
  lazyLoadingModule = signal<loadType | null>(null);
  routes = [
    { path: '', name: 'Home', permission: '' },
    { path: 'login', name: 'Login', permission: '' },
    { path: 'profile', name: 'Profile', permission: '' },
    { path: 'about', name: 'About', permission: '' },
    { path: 'contact', name: 'Contact', permission: '' },
    { path: 'news', name: 'News', permission: '' },
    { path: 'privacy', name: 'Privacy', permission: '' },
    { path: 'terms', name: 'Terms', permission: '' },
    { path: 'books', name: 'Books', permission: '' },
    { path: 'users', name: 'Users', permission: ['SuperAdmin', 'Admin'] },
    { path: 'flags', name: 'Flags', permission: ['SuperAdmin', 'Admin'] },
    { path: 'formats', name: 'Formats', permission: ['SuperAdmin', 'Admin'] },
    { path: 'resources', name: 'My resources', permission: '' },
    { path: 'shelves', name: 'Shelves', permission: '' },
    {
      path: 'categories',
      name: 'Categories',
      permission: ['SuperAdmin', 'Admin'],
    },
    { path: 'get-involved', name: 'Get Involved', permission: '' },
    { path: 'create-resource', name: 'Create Resource', permission: '' },
    { path: 'documentation', name: 'Documentation', permission: '' },
    { path: 'changelog', name: 'Updates', permission: '' },
  ];

  drawerOpen = false;
  quickActionsOpen = false;
  readonly quickActions: QuickAction[] = [
    {
      label: 'Search',
      path: '/search',
      icon: 'M16 10a6 6 0 1 1-12 0 6 6 0 0 1 12 0Zm-1 5 6 6',
      search: true,
    },
    {
      label: 'My resources',
      path: '/resources',
      icon: 'M4 3h6a2 2 0 0 1 2 2v16a3 3 0 0 0-3-3H4V3Zm16 0h-6a2 2 0 0 0-2 2v16a3 3 0 0 1 3-3h5V3Z',
    },
    {
      label: 'Shelves',
      path: '/shelves',
      icon: 'M3 3v18M21 3v18M3 12h18M3 20h18M6 5v7M10 4v8M14 6l3 6M7 15v5M12 14v6M17 15v5',
    },
    {
      label: 'Add resource',
      path: '/resources/create',
      icon: 'M12 5v14M5 12h14',
    },
    {
      label: 'Support',
      path: 'https://ko-fi.com/studyheroes',
      icon: 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z',
      external: true,
    },
  ];
  readonly desktopQuickActions = [...this.quickActions].reverse();
  readonly mobileQuickActions = [
    this.quickActions[1],
    this.quickActions[2],
    this.quickActions[0],
    this.quickActions[3],
    this.quickActions[4],
  ];
  @ViewChild('quickActionsToggle')
  quickActionsToggle?: ElementRef<HTMLButtonElement>;

  @HostListener('document:keydown.escape')
  closeQuickActions() {
    if (!this.quickActionsOpen) return;
    this.quickActionsOpen = false;
    this.quickActionsToggle?.nativeElement.focus();
  }

  constructor(
    private router: Router,
    private profileFacade: ProfileFacadeService,
    private seoService: SeoService,
  ) {
    this.seoService.initialize();

    combineLatest([
      this.router.events.pipe(
        filter(
          (event) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        map((event) => event instanceof NavigationStart),
        startWith(false),
      ),
      toObservable(this.profileFacade.firstLoad),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([loadingPage, loadingProfile]) => {
        this.lazyLoadingModule.set(
          loadingProfile ? 'profile' : loadingPage ? 'module' : null,
        );
      });

    if (environment.useFirebaseEmulator) {
      connectAuthEmulator(inject(Auth), 'http://127.0.0.1:9099');
    }
  }

  toggleDrawer() {
    var checkbox = document.getElementById('side-drawer') as HTMLInputElement;
    if (checkbox) {
      checkbox.checked = !checkbox.checked;
    }
  }

  openAppModal() {
    const modal = document.getElementById('links_modal') as HTMLDialogElement;
    if (modal) {
      modal.showModal();
    }
    this.toggleDrawer(); // Close drawer when opening modal
  }

  openSearchModal() {
    this.quickActionsOpen = false;
    this.router.navigate(['/search'], { queryParams: { autoFocus: true } });
  }

  addResource() {
    this.quickActionsOpen = false;
    this.router.navigate(['/resources/create']);
  }
}
